import React, { useState } from "react";
import {
  TextAreaComponent,
  ValidationRule,
} from "../../services/types/ComponentTypes";

interface TextAreaProps {
  config: TextAreaComponent;
  value?: string;
  onChange?: (name: string, value: string) => void;
  externalError?: string;
}

export const TextArea: React.FC<TextAreaProps> = ({
  config,
  value = "",
  onChange,
  externalError,
}) => {
  const [error, setError] = useState<string>("");
  const [isFocused, setIsFocused] = useState(false);

  const validateField = (inputValue: string): string => {
    if (!config.validation) return "";

    for (const rule of config.validation) {
      switch (rule.rule) {
        case ValidationRule.REQUIRED:
          if (!inputValue.trim())
            return rule.message || "This field is required";
          break;
        case ValidationRule.MIN_LENGTH:
          if (inputValue.length < (rule.value as number))
            return rule.message || `Minimum ${rule.value} characters required`;
          break;
        case ValidationRule.MAX_LENGTH:
          if (inputValue.length > (rule.value as number))
            return rule.message || `Maximum ${rule.value} characters allowed`;
          break;
      }
    }
    return "";
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    const validationError = validateField(newValue);
    setError(validationError);
    onChange?.(config.name, newValue);
  };

  const handleBlur = () => {
    setIsFocused(false);
    const validationError = validateField(value);
    setError(validationError);
  };

  const getWidthClass = () => {
    if (config.style?.widthMode === "wrap") return "w-auto";
    return "w-full";
  };

  const textareaStyles: React.CSSProperties = {
    borderRadius: config.style?.borderRadius || 8,
    fontSize: config.style?.fontSize || 14, // Standardized to 14px (text-sm)
    fontWeight: config.style?.fontWeight || "normal",
    // Only set color/backgroundColor if explicitly provided, otherwise let Tailwind handle it
    ...(config.style?.textColor && !config.disabled ? { color: config.style.textColor } : {}),
    ...(config.style?.backgroundColor && !config.disabled ? { backgroundColor: config.style.backgroundColor } : {}),
    height: `${(config.rows || 4) * 9}px`,
    fontFamily: "inherit", // Use system font stack
    textAlign: config.style?.textAlign || "left",
  };

  const characterCountColor =
    config.maxLength && value.length > config.maxLength * 0.8
      ? "text-amber-500 dark:text-amber-400"
      : "text-gray-500 dark:text-gray-400";

  return (
    <div className={`${getWidthClass()} px-1`} data-field-name={config.name}>
      {config.label && (
        <label className="block mb-0.5 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
          {config.label}
          {config.required && <span className="text-danger-500 dark:text-danger-400 ml-1">*</span>}
        </label>
      )}

      <textarea
        name={config.name}
        placeholder={config.placeholder}
        value={value}
        onChange={handleChange}
        onFocus={() => setIsFocused(true)}
        onBlur={handleBlur}
        disabled={config.disabled}
        maxLength={config.maxLength}
        rows={config.rows || 4}
        className={`w-full py-2 px-3 text-sm font-normal text-left border border-1 outline-none resize-y font-sans transition-colors duration-200 dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 ${config.disabled ? "cursor-not-allowed opacity-70 bg-gray-100 dark:bg-gray-700" : "cursor-text"
          } ${error
            ? "border-danger-500 dark:border-danger-500"
            : isFocused
              ? "border-primary-500 dark:border-primary-400"
              : "border-gray-300 dark:border-gray-600"
          } focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:outline-none`}
        style={textareaStyles}
      />

      {config.maxLength && (
        <div className={`text-right text-xs mt-1 ${characterCountColor} dark:text-gray-400`}>
          {value.length}/{config.maxLength} characters
        </div>
      )}

      {(error || externalError) && (
        <div className="text-danger-500 dark:text-danger-400 text-xs mt-1">
          {error || externalError}
        </div>
      )}

      {config.helperText && !error && !externalError && (
        <div className="text-gray-500 dark:text-gray-400 text-xs mt-1">{config.helperText}</div>
      )}
    </div>
  );
};
